import React, { useState, useMemo, useCallback } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Flex,
  Alert,
  AlertIcon,
  Collapse,
  IconButton,
  Input,
  Select,
  Button,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronUp, FiDownload, FiRefreshCw } from 'react-icons/fi';
import moment from 'moment';
import { useCampaignLogs } from '../../../hooks/useCampaignLogs';
import { COLOR_MAP, STATUS_COLOR } from '../../../components/CRM/Campaigns/campaignConstants';

function StepTimeline({ logs }) {
  return (
    <VStack align="stretch" spacing={0} pl={2} pt={2}>
      {logs.map((log, idx) => {
        const color = COLOR_MAP[log.subType] || 'gray';
        const isLast = idx === logs.length - 1;
        const summary = log.executionResult?.summary || log.subType || 'Unknown step';
        const details = log.executionResult?.details || {};

        return (
          <Box
            key={log.id}
            borderLeft="3px solid"
            borderColor={`${color}.400`}
            pl={4}
            pb={isLast ? 0 : 4}
            position="relative"
          >
            <Box
              position="absolute"
              left="-7px"
              top="2px"
              w="11px"
              h="11px"
              borderRadius="full"
              bg={`${color}.400`}
            />

            <HStack mb={1} flexWrap="wrap">
              <Badge colorScheme={color} fontSize="xs">
                {(log.subType || 'UNKNOWN').replace(/_/g, ' ')}
              </Badge>
              <Badge colorScheme={STATUS_COLOR[log.status] || 'gray'} fontSize="xs" variant="outline">
                {log.status}
              </Badge>
              {log.startedAt && (
                <Text fontSize="xs" color="gray.500">
                  {moment(log.startedAt).format('MMM D, h:mm A')}
                </Text>
              )}
            </HStack>

            <Text fontSize="sm" mb={1}>{summary}</Text>

            {/* Show resolved content preview */}
            {details.to && (
              <Text fontSize="xs" color="gray.400">To: {details.to}</Text>
            )}
            {details.subject && (
              <Text fontSize="xs" color="gray.400">Subject: {details.subject}</Text>
            )}

            {log.errorLog && (
              <Alert status="error" mt={1} mb={1} borderRadius="md" py={1} px={2}>
                <AlertIcon boxSize={4} />
                <Text fontSize="xs">{log.errorLog}</Text>
              </Alert>
            )}

            {log.completedAt && log.startedAt && (
              <Text fontSize="xs" color="gray.400">
                Duration: {moment(log.completedAt).diff(moment(log.startedAt), 'seconds')}s
              </Text>
            )}
          </Box>
        );
      })}
    </VStack>
  );
}

function ContactRow({ contactId, entry, totalRuns, isExpanded, onToggle }) {
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const expandedBg = useColorModeValue('gray.50', 'gray.800');
  const latest = entry.latestLog;
  const contact = entry.contact;
  const name = contact
    ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unknown'
    : 'Unknown Contact';
  const email = contact?.email || '';
  const runLabel = totalRuns > 1 ? ` — Run ${entry.runIndex + 1}` : '';

  const statusColor = latest ? STATUS_COLOR[latest.status] || 'gray' : 'gray';
  const stepColor = latest?.subType ? COLOR_MAP[latest.subType] || 'gray' : 'gray';

  return (
    <>
      <Tr cursor="pointer" onClick={onToggle} _hover={{ bg: hoverBg }}>
        <Td>
          <Text fontSize="sm" fontWeight="medium">
            {name}
            {runLabel && <Text as="span" color="gray.500" fontWeight="normal">{runLabel}</Text>}
          </Text>
          {email && <Text fontSize="xs" color="gray.500">{email}</Text>}
        </Td>
        <Td>
          {latest?.subType ? (
            <Badge colorScheme={stepColor} fontSize="xs">
              {latest.subType.replace(/_/g, ' ')}
            </Badge>
          ) : (
            <Text fontSize="xs" color="gray.400">--</Text>
          )}
        </Td>
        <Td>
          <Badge colorScheme={statusColor} fontSize="xs">{latest?.status || 'unknown'}</Badge>
        </Td>
        <Td>
          <Text fontSize="xs" color="gray.500">
            {entry.logs[0]?.createdAt ? moment(entry.logs[0].createdAt).fromNow() : '--'}
          </Text>
        </Td>
        <Td>
          <Text fontSize="xs" color="gray.500">
            {latest?.updatedAt ? moment(latest.updatedAt).fromNow() : '--'}
          </Text>
        </Td>
        <Td isNumeric>
          <IconButton
            icon={isExpanded ? <FiChevronUp /> : <FiChevronDown />}
            size="xs"
            variant="ghost"
            aria-label="Toggle details"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
          />
        </Td>
      </Tr>
      <Tr>
        <Td colSpan={6} p={0} borderBottom={isExpanded ? undefined : 'none'}>
          <Collapse in={isExpanded} animateOpacity>
            <Box px={4} py={3} bg={expandedBg}>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2}>
                EXECUTION TIMELINE ({entry.stepCount} steps)
              </Text>
              <StepTimeline logs={entry.logs} />
            </Box>
          </Collapse>
        </Td>
      </Tr>
    </>
  );
}

export default function CampaignLogsDrawer({
  isOpen,
  onClose,
  userCampaignId,
  templateId,
  campaignName,
}) {
  const { logs, contactSummaries, stats, loading, error, reload } = useCampaignLogs({
    userCampaignId: isOpen ? userCampaignId : null,
    templateId: isOpen ? templateId : null,
  });

  const [expandedContactId, setExpandedContactId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const toggleContact = (cid) => {
    setExpandedContactId((prev) => (prev === cid ? null : cid));
  };

  const filteredEntries = useMemo(() => {
    let entries = Array.from(contactSummaries.entries());

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      entries = entries.filter(([, entry]) => {
        const c = entry.contact;
        if (!c) return false;
        return (
          (c.firstName || '').toLowerCase().includes(q) ||
          (c.lastName || '').toLowerCase().includes(q) ||
          (c.email || '').toLowerCase().includes(q)
        );
      });
    }

    if (statusFilter !== 'all') {
      entries = entries.filter(([, entry]) => {
        return entry.latestLog?.status === statusFilter;
      });
    }

    return entries;
  }, [contactSummaries, searchQuery, statusFilter]);

  const runCountByContact = useMemo(() => {
    const counts = {};
    for (const [key] of contactSummaries) {
      const cid = key.split('__run_')[0];
      counts[cid] = (counts[cid] || 0) + 1;
    }
    return counts;
  }, [contactSummaries]);

  const exportCSV = useCallback(() => {
    const rows = [['Contact', 'Email', 'Step', 'Status', 'Started', 'Completed', 'Summary']];
    logs.forEach((log) => {
      const c = log.contact || {};
      rows.push([
        `${c.firstName || ''} ${c.lastName || ''}`.trim(),
        c.email || '',
        log.subType || '',
        log.status || '',
        log.startedAt || '',
        log.completedAt || '',
        log.executionResult?.summary || '',
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(campaignName || 'campaign').replace(/\s+/g, '_')}_logs.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [logs, campaignName]);

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="xl" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <Flex justify="space-between" align="center" pr={8}>
            <Text fontSize="lg" fontWeight="bold">
              {campaignName || 'Campaign'} — Logs
            </Text>
            <HStack spacing={2}>
              <Tooltip label="Refresh">
                <IconButton
                  icon={<FiRefreshCw />}
                  size="sm"
                  variant="ghost"
                  onClick={reload}
                  aria-label="Refresh logs"
                />
              </Tooltip>
              <Tooltip label="Export CSV">
                <IconButton
                  icon={<FiDownload />}
                  size="sm"
                  variant="ghost"
                  onClick={exportCSV}
                  aria-label="Export logs"
                  isDisabled={!logs.length}
                />
              </Tooltip>
            </HStack>
          </Flex>
        </DrawerHeader>

        <DrawerBody pt={4}>
          {loading && (
            <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
          )}

          {error && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          {!loading && !error && (
            <VStack spacing={5} align="stretch">
              {/* Stats */}
              <StatGroup>
                <Stat>
                  <StatLabel>Total</StatLabel>
                  <StatNumber>{stats.total}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>In Progress</StatLabel>
                  <StatNumber color="blue.500">{stats.inProgress}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Completed</StatLabel>
                  <StatNumber color="green.500">{stats.completed}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Failed</StatLabel>
                  <StatNumber color="red.500">{stats.failed}</StatNumber>
                </Stat>
              </StatGroup>

              {/* Filters */}
              <HStack spacing={3}>
                <Input
                  size="sm"
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  maxW="250px"
                />
                <Select
                  size="sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  maxW="160px"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="paused">Paused</option>
                </Select>
              </HStack>

              {/* Contact table */}
              {filteredEntries.length === 0 ? (
                <Text textAlign="center" color="gray.500" py={8}>
                  {contactSummaries.size === 0
                    ? 'No executions yet. Contacts will appear here once the campaign processes them.'
                    : 'No results match your filters.'}
                </Text>
              ) : (
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Contact</Th>
                        <Th>Current Step</Th>
                        <Th>Status</Th>
                        <Th>Started</Th>
                        <Th>Last Update</Th>
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredEntries.map(([key, entry]) => {
                        const cid = key.split('__run_')[0];
                        return (
                          <ContactRow
                            key={key}
                            contactId={cid}
                            entry={entry}
                            totalRuns={runCountByContact[cid] || 1}
                            isExpanded={expandedContactId === key}
                            onToggle={() => toggleContact(key)}
                          />
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
